import api from "../api/axios";

// Procedure item in API response
export interface CategoryProcedure {
  customProcedureId: number;
  cdtCodeId: number;
  codes: string[]; // Array of code strings like ["D0120", "D0140"]
  name: string;
  description: string;
  fee: number;
  durationMinutes: number;
}

// Category with procedures from API response
export interface ProcedureCategory {
  category: string;
  procedures: CategoryProcedure[];
}

// Legacy interface for backward compatibility
export interface ProcedureCDTCode {
  id: number;
  code: string;
  fee: number;
  durationMinutes: number;
  description: string;
}

// Custom procedure for internal use
export interface CustomProcedure {
  id: number;
  cdtCodeId: number;
  codes: string[];
  name: string;
  description: string;
  fee: number;
  durationMinutes: number;
}

// Legacy interface - keeping for compatibility
export interface OrganizationProcedure {
  id: number;
  cdtCode: string;
  name: string;
  description: string;
  durationMinutes: number;
  fee: number;
  category: string;
  isCustom: boolean;
}

// Request body for creating custom procedure
export interface CreateCustomProcedureRequest {
  codeIds: number[];
  name: string;
  description?: string;
  fee: number;
  durationMinutes: number;
}

// Request body for updating custom procedure
export interface UpdateCustomProcedureRequest {
  codeIds?: number[];
  name?: string;
  description?: string;
  fee?: number;
  durationMinutes?: number;
}

// Request body for updating only fee and duration (preserves CDT codes)
export interface UpdateProcedureDurationFeeRequest {
  fee: number;
  durationMinutes: number;
}

// Import fees error from API
export interface ImportFeesError {
  rowNumber: number;
  cdtCode: string;
  errorMessage: string;
}

// Import fees result from API
export interface ImportFeesResult {
  successCount: number;
  failedCount: number;
  errors: ImportFeesError[];
}

export const organizationService = {
  // Get all procedures for the organization (grouped by category)
  async getProcedures(search?: string): Promise<ProcedureCategory[]> {
    const params = new URLSearchParams();
    if (search) {
      params.append("search", search);
    }
    const queryString = params.toString();
    const url = `/organization/procedures${
      queryString ? `?${queryString}` : ""
    }`;
    const response = await api.get<ProcedureCategory[]>(url);
    return response.data || [];
  },

  // Create custom procedure - POST /organization/procedures
  async createCustomProcedure(
    params: CreateCustomProcedureRequest,
  ): Promise<number> {
    const response = await api.post<number>("/organization/procedures", params);
    return response.data;
  },

  // Update custom procedure - PUT /organization/procedures/{id}
  async updateCustomProcedure(
    procedureId: number,
    params: UpdateCustomProcedureRequest,
  ): Promise<void> {
    await api.put(`/organization/procedures/${procedureId}`, params);
  },

  // Update procedure fee and duration only - PUT /organization/procedures/{id}/duration-fee
  // Use this for inline fee/duration edits to avoid clearing associated CDT codes.
  async updateProcedureDurationFee(
    procedureId: number,
    params: UpdateProcedureDurationFeeRequest,
  ): Promise<void> {
    await api.put(`/organization/procedures/${procedureId}/duration-fee`, params);
  },

  // Delete custom procedure - DELETE /organization/procedures/{id}
  async deleteCustomProcedure(procedureId: number): Promise<void> {
    await api.delete(`/organization/procedures/${procedureId}`);
  },

  // Update CDT code duration - PUT /cdt-codes/{id}/duration
  async updateCdtCodeDuration(
    cdtCodeId: number,
    durationMinutes: number,
  ): Promise<void> {
    await api.put(`/cdt-codes/${cdtCodeId}/duration`, { durationMinutes });
  },

  // Update CDT code fee - PUT /cdt-codes/{id}/fee
  async updateCdtCodeFee(cdtCodeId: number, fee: number): Promise<void> {
    await api.put(`/cdt-codes/${cdtCodeId}/fee`, { fee });
  },

  // Import fees from CSV/XLSX file - POST /organization/fees/import
  async importFees(file: File): Promise<ImportFeesResult> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post<ImportFeesResult>(
      "/organization/fees/import",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  },
};
